import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Row,
  Space,
  theme,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import {
  PosTimePicker,
  type PosTimePickerProps,
} from 'pos-client/components/shared/Form/PosTimePicker.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type LocationManifestUpdateFormModel,
  LocationManifestUpdateFormSchema,
  type LocationModel,
} from 'pos-shared/models/locationSchema.ts';

export const LocationManifest = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { showSuccess } = useNotifications();
  const { location } = useOutletContext<{
    location: LocationModel;
  }>();
  const trpcContext = trpc.useUtils();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    watch,
    reset,
    setValue,
  } = useForm<LocationManifestUpdateFormModel>({
    shouldFocusError: true,
    resolver: zodResolver(LocationManifestUpdateFormSchema),
    defaultValues: {
      locationId: location.id,
      configName: 'staticFinalManifestRunTime',
      isDefault: true,
    },
  });

  const formValues = watch();

  const {
    data: locationConfigurations,
    isLoading: isLoadingLocationConfigurations,
  } = trpc.serviceLocation.getServiceLocationConfigV2.useQuery(
    {
      listOfServiceLocationIds: [location?.id as string],
      listOfNames: ['staticFinalManifestRunTime'],
    },
    {
      enabled: !!location?.id,
    }
  );

  const { mutate: updateManifest, isLoading } =
    trpc.serviceLocation.updateServiceLocationManifestConfig.useMutation({
      onSuccess: async () => {
        const formValues = watch();
        await trpcContext.serviceLocation.getServiceLocationConfigV2.invalidate(
          {
            listOfServiceLocationIds: [location?.id as string],
            listOfNames: ['staticFinalManifestRunTime'],
          }
        );

        reset({
          ...formValues,
          value: formValues.isDefault ? '' : formValues.value,
        });

        showSuccess(
          t(`common.notification.success.message`, {
            action: 'updated',
            entity: 'location manifest',
          })
        );
      },
    });

  const handleCancel = () => {
    reset();
  };

  const handleManifestSave = handleSubmit(() => {
    const formValues = watch();

    updateManifest({
      locationId: location.id,
      configId: formValues.configId,
      configName: formValues.configName,
      value: formValues.value.format('HH:mm'),
      isDefault: formValues.isDefault,
    });
  });

  const defaultTimePickerProps: Partial<PosTimePickerProps> = {
    use12Hours: true,
    showNow: false,
    needConfirm: true,
    style: fullWidth,
    allowClear: false,
  };

  useEffect(() => {
    if (locationConfigurations?.results) {
      const staticValue = locationConfigurations.results.find(
        (config) => config.name === 'staticFinalManifestRunTime'
      );

      if (staticValue) {
        reset({
          isDefault: false,
          value: dayjs(staticValue.value, 'HH:mm').isValid()
            ? dayjs(staticValue.value, 'HH:mm')
            : undefined,
          locationId: location.id,
          configId: staticValue.id,
          configName: 'staticFinalManifestRunTime',
        });
      } else {
        reset({
          isDefault: true,
          locationId: location.id,
          configName: 'staticFinalManifestRunTime',
        });
      }
    }
  }, [locationConfigurations]);

  return (
    <PosCard
      headerProps={{
        title: t('locations.manifest.title'),
        icon: faCalendarClock,
      }}
      colProps={{
        span: 24,
      }}
    >
      <Form layout="vertical">
        <Row>
          <Typography.Text>
            {t('locations.manifest.serviceTypeStatic')}
          </Typography.Text>
        </Row>
        <Row style={{ marginTop: token.margin }}>
          <Checkbox
            checked={!!formValues.isDefault}
            onChange={() => setValue('isDefault', true, { shouldDirty: true })}
          >
            {t('locations.manifest.defaultRunTime')}
          </Checkbox>
        </Row>
        <Row
          // The set height is the height of the input to stop CLS
          style={{ marginTop: token.marginXS, height: '32px' }}
          align="middle"
        >
          <Col>
            <Checkbox
              checked={!formValues.isDefault}
              onChange={() => setValue('isDefault', false)}
            >
              {t('locations.manifest.overwriteRunTime')}
            </Checkbox>
          </Col>
          {!formValues.isDefault && (
            <Col>
              <PosTimePicker
                control={control}
                name="value"
                disabled={isLoadingLocationConfigurations}
                noStyle
                {...defaultTimePickerProps}
              />
            </Col>
          )}
        </Row>
        <Space size="small" style={{ marginTop: token.marginMD }}>
          <Button
            type="default"
            onClick={handleCancel}
            disabled={isLoading || !isDirty}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="primary"
            disabled={!isDirty || !isValid || isLoading}
            onClick={handleManifestSave}
          >
            {t('common.buttons.saveChanges')}
          </Button>
        </Space>
      </Form>
    </PosCard>
  );
};
